import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import { isAiMateUnifiedHelpIntegrationEnabled } from '@helpCenter/feature-flags';
import { useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';
import SendIcon from '@atlaskit/icon/glyph/send';
import { Inline } from '@atlaskit/primitives';
import { N0, N200, N40A, N60A, N500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { ExperienceType } from '@atlassian/conversation-assistant';
import { XL_MIN_WIDTH, DESKTOP_MIN_WIDTH } from '../../common/constants';
import messages from '../../pages/messages';
import { useActions, useConversation, useIsLoading } from '../../store/converse';
import { isAssistantMessageNotIdle } from '../../utils/ai-mate/is-assistant-message-not-idle';
import { getAnalyticsWebClient } from '../../utils/analytics';
import { getProductIdentifierAttributes } from '../../utils/analytics/get-product-identifier-attributes';
import { Flipped } from '../../utils/flip-toolkit';
import type { EAPConversationInputUser$key } from './__generated__/EAPConversationInputUser.graphql';
import { DisclaimerFooter } from './disclaimer-footer';

export const EAPConversationInput: React.VFC<{
    userRef: EAPConversationInputUser$key;
}> = ({ userRef }) => {
    const [term, setTerm] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const { formatMessage } = useIntl();
    const [conversation] = useConversation();
    const [isLoading] = useIsLoading();
    const {
        startNewConversation,
        addHelpseekerMessage,
        startNewConversationWithAiMate,
        addHelpseekerMessageWithAiMate,
    } = useActions();
    const analyticsClient = getAnalyticsWebClient();

    const userData = useFragment(
        graphql`
            fragment EAPConversationInputUser on User {
                accountId
            }
        `,
        userRef
    );

    const { accountId } = userData;

    const hasConversation = !!conversation;

    const isInputDisabled =
        (conversation &&
            (isAssistantMessageNotIdle(conversation.history[conversation.history.length - 1]) || isLoading)) ??
        false;

    const onSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            if (isInputDisabled) {
                return;
            }

            const query = term.trim();

            if (query.length > 0) {
                if (isAiMateUnifiedHelpIntegrationEnabled()) {
                    if (hasConversation) {
                        addHelpseekerMessageWithAiMate(query, { accountId });
                    } else {
                        startNewConversationWithAiMate(query, { accountId });
                    }
                } else if (hasConversation) {
                    addHelpseekerMessage(query, { accountId });
                } else {
                    startNewConversation(query, { accountId });
                }

                setTerm('');
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isInputDisabled, term, accountId, addHelpseekerMessage, hasConversation, startNewConversation]
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const handleChange = useCallback((event) => setTerm(event.currentTarget.value), []);

    const fireInputSelectedAnalytics = useCallback(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        analyticsClient.sendUIEvent({
            source: 'uhConversationScreen',
            action: 'clicked',
            actionSubject: 'input',
            actionSubjectId: 'conversationInput',
            attributes: {
                ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sendIconColor = () => {
        if (isInputDisabled) {
            return token('color.icon.disabled', N60A);
        }
        if (isFocused) {
            return token('color.icon.selected', '#0C66E4');
        }
        return token('color.icon.subtle', N500);
    };

    return (
        <Container>
            <Inline grow="fill" space="space.150" alignBlock="center">
                <Form onSubmit={onSubmit}>
                    <Flipped flipId="ConversationInput">
                        <InputWrapper isHighlighted={isFocused && !isInputDisabled}>
                            <Input
                                type="text"
                                name="term"
                                autoComplete="off"
                                // eslint-disable-next-line jsx-a11y/no-autofocus
                                autoFocus
                                spellCheck={false}
                                placeholder={
                                    !conversation
                                        ? formatMessage(messages.noConversationPlaceholder)
                                        : formatMessage(messages.unifiedHelpSearchPlaceholderInConversation)
                                }
                                value={term}
                                onChange={handleChange}
                                onClick={fireInputSelectedAnalytics}
                                onFocus={useCallback(() => {
                                    setIsFocused(true);
                                    fireInputSelectedAnalytics();
                                }, [fireInputSelectedAnalytics])}
                                isInputDisabled={isInputDisabled}
                                onBlur={useCallback(() => {
                                    setIsFocused(false);
                                }, [])}
                            />

                            <UnstyledButton onClick={onSubmit} disabled={isInputDisabled}>
                                <SendIcon
                                    label={formatMessage(messages.submitQueryIconLabel)}
                                    primaryColor={sendIconColor()}
                                />
                            </UnstyledButton>
                        </InputWrapper>
                    </Flipped>
                </Form>
            </Inline>
            <DisclaimerFooter />
        </Container>
    );
};

const Form = styled.form({
    flex: '1 0 auto',
});

const Input = styled.input<{ isInputDisabled: boolean }>({
    fontSize: '14px',
    fontFamily: 'sans-serif',
    width: '100%',
    boxSizing: 'border-box',
    border: 0,
    outline: 'none',
    backgroundColor: 'transparent',
    '::placeholder': {
        color: ({ isInputDisabled }) =>
            isInputDisabled ? token('color.text.disabled', N60A) : token('color.text.subtlest', N200),
    },
});

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.5s ease',
    paddingLeft: token('space.150', '12px'),
    paddingRight: `calc(${token('space.800', '64px')} + ${token('space.050', '4px')})`,
    [`@media only screen and (min-width: ${DESKTOP_MIN_WIDTH}px)`]: {
        paddingLeft: `calc(20vw  + ${token('space.400', '32px')} + ${token('space.025', '2px')} + ${token(
            'space.150',
            '12px'
        )})`,
        paddingRight: `calc(20vw  + ${token('space.400', '32px')})`,
    },

    [`@media only screen and (min-width: ${XL_MIN_WIDTH}px)`]: {
        paddingLeft: `calc(30vw  + ${token('space.400', '32px')} + ${token('space.025', '2px')} + ${token(
            'space.150',
            '12px'
        )})`,
        paddingRight: `calc(30vw  + ${token('space.400', '32px')})`,
    },
});

const InputWrapper = styled.div<{ isHighlighted: boolean }>({
    backgroundColor: token('color.background.input', N0),
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '5px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: token('space.150', '12px'),
    borderColor: ({ isHighlighted }) =>
        isHighlighted ? token('color.border.selected', '#0C66E4') : token('color.border', N40A),
});

const UnstyledButton = styled.button<{ disabled: boolean }>({
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 0,
    ':hover': {
        cursor: ({ disabled }) => (disabled ? 'not-allowed' : 'pointer'),
    },
});
